import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useSWR, { mutate } from "swr";
import { toast } from "sonner";
import {
  LayoutDashboard, CalendarCheck, DollarSign, Users, LogOut, Image as ImageIcon,
  Clock, FileText, Tag, Phone, Plus, Trash2, Save, MessageSquare, Star, Pencil, X, CalendarOff, CalendarDays, ChevronLeft, ChevronRight, Gift, Download, Upload, Percent, ShieldCheck, QrCode, Copy, Bell, Send, RefreshCw,
} from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { DEFAULT_PRIVACY, DEFAULT_REFUND } from "@/lib/policies";
import { NewReservationModal } from "@/components/admin/NewReservationModal";

const resBadge = (r) => r.status === "cancelled" ? ["Cancelled", "bg-[#2a1a12] text-[#ff5147]"]
  : r.payment_status === "check_due" ? ["Unpaid – check due", "bg-[#2a1a12] text-[#f6b64a]"]
  : r.payment_status === "paid" ? [r.payment_method === "check" ? "Paid (check)" : "Confirmed", "bg-[#14241f] text-[#2fd0c6]"]
  : r.status === "confirmed" ? ["Confirmed", "bg-[#2a2114] text-[#8fa5a5]"] : ["Pending", "bg-[#2a2114] text-[#8fa5a5]"];
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";

const fetcher = (url) => api.get(url).then((r) => r.data);

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "reservations", label: "Reservations", icon: CalendarCheck },
  { id: "calendar", label: "Calendar", icon: CalendarDays },
  { id: "contacts", label: "Messages", icon: MessageSquare },
  { id: "giftcards", label: "Gift Cards", icon: Gift },
  { id: "waivers", label: "Waivers", icon: ShieldCheck },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "promos", label: "Promo Codes", icon: Percent },
  { id: "reviews", label: "Reviews", icon: Star },
  { id: "pricing", label: "Pricing", icon: DollarSign },
  { id: "hours", label: "Hours", icon: Clock },
  { id: "blackouts", label: "Closed Dates", icon: CalendarOff },
  { id: "gallery", label: "Gallery", icon: ImageIcon },
  { id: "agreement", label: "Agreement", icon: FileText },
  { id: "policies", label: "Privacy & Refunds", icon: ShieldCheck },
  { id: "discount", label: "HERO Discount", icon: Tag },
  { id: "contact", label: "Contact Info", icon: Phone },
];

const inp = "w-full bg-[#221a12] border border-[#33281c] rounded-xl px-4 py-2.5 text-[#f6ecd4] outline-none focus:border-[#ff5147] focus:bg-[#2a2011] transition";

export default function AdminDashboard() {
  const { admin, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => { if (ready && !admin) navigate("/admin/login"); }, [ready, admin, navigate]);
  if (!ready || !admin) return <div className="min-h-screen grid place-items-center text-[#8fa5a5]">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#16120e] flex">
      <Seo title="Admin Dashboard | Keys Event Center" />
      <aside className="w-60 shrink-0 bg-[#1c150f] border-r border-[#33281c] p-5 hidden md:flex flex-col fixed h-screen">
        <div className="mb-8"><img src="/brand/logo-badge.png" alt="Keys Event Center" className="h-28 w-auto object-contain" /></div>
        <nav className="space-y-1 flex-1 overflow-y-auto">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition ${tab === t.id ? "bg-[#2a1a12] text-[#ff5147]" : "text-[#a9bdbd] hover:text-[#f6ecd4] hover:bg-[#16120e]"}`}>
              <t.icon size={17} /> {t.label}
            </button>
          ))}
        </nav>
        <button onClick={() => { logout(); navigate("/admin/login"); }} className="flex items-center gap-2 text-sm text-[#a9bdbd] hover:text-[#ff5147] px-3 py-2.5" data-testid="admin-logout"><LogOut size={17} /> Logout</button>
      </aside>

      <main className="flex-1 min-w-0 md:ml-60 p-4 sm:p-8">
        <div className="md:hidden sticky top-0 z-40 -mx-4 px-4 pt-3 pb-3 mb-4 bg-[#16120e]/95 backdrop-blur border-b border-[#33281c]">
          <div className="flex items-center justify-between mb-3">
            <img src="/brand/logo-badge.png" alt="Keys Event Center" className="h-10 w-auto object-contain" />
            <button onClick={() => { logout(); navigate("/admin/login"); }} className="flex items-center gap-1.5 text-xs font-semibold text-[#a9bdbd] hover:text-[#ff5147]" data-testid="admin-logout-mobile"><LogOut size={15} /> Logout</button>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
            {TABS.map((t) => (<button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-m-${t.id}`} className={`shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold transition ${tab === t.id ? "bg-[#ff5147] text-white" : "bg-[#221a12] border border-[#33281c] text-[#a9bdbd]"}`}><t.icon size={14} /> {t.label}</button>))}
          </div>
        </div>
        {tab === "overview" && <Overview />}
        {tab === "reservations" && <Reservations />}
        {tab === "calendar" && <CalendarTab />}
        {tab === "contacts" && <Contacts />}
        {tab === "giftcards" && <GiftCardsAdmin />}
        {tab === "waivers" && <WaiversAdmin />}
        {tab === "notifications" && <TelegramNotifications />}
        {tab === "promos" && <DiscountCodesEditor />}
        {tab === "reviews" && <ReviewsEditor />}
        {tab === "pricing" && <PricingEditor />}
        {tab === "hours" && <HoursEditor />}
        {tab === "blackouts" && <BlackoutEditor />}
        {tab === "gallery" && <GalleryEditor />}
        {tab === "agreement" && <AgreementEditor />}
        {tab === "policies" && <><PolicyEditor section="privacy" title="Privacy Policy" defaults={DEFAULT_PRIVACY} /><div className="h-8" /><PolicyEditor section="refund_policy" title="Cancellations & Refunds" defaults={DEFAULT_REFUND} /></>}
        {tab === "discount" && <DiscountEditor />}
        {tab === "contact" && <ContactEditor />}
      </main>
    </div>
  );
}

function Header({ title, sub }) {
  return <div className="mb-6"><h1 className="font-display text-2xl sm:text-3xl font-bold text-[#f6ecd4]">{title}</h1>{sub && <p className="text-[#8fa5a5] mt-1 text-sm">{sub}</p>}</div>;
}

function Overview() {
  const { data: stats } = useSWR("/admin/stats", fetcher);
  const cards = [
    { label: "Total Reservations", value: stats?.total_reservations ?? "-", icon: CalendarCheck, color: "#ff5147" },
    { label: "Confirmed & Paid", value: stats?.confirmed ?? "-", icon: Users, color: "#2fd0c6" },
    { label: "Revenue", value: stats ? `$${stats.revenue}` : "-", icon: DollarSign, color: "#f6b64a" },
    { label: "Messages", value: stats?.contacts ?? "-", icon: MessageSquare, color: "#2fd0c6" },
  ];
  return (
    <div>
      <Header title="Overview" sub="Your venue at a glance." />
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="card p-6" data-testid={`stat-${c.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <c.icon size={22} style={{ color: c.color }} />
            <div className="font-mono-arcade text-3xl font-extrabold mt-3 text-[#f6ecd4]">{c.value}</div>
            <div className="text-sm text-[#8fa5a5] mt-1">{c.label}</div>
          </div>
        ))}
      </div>
      <BookeoHealth />
    </div>
  );
}

function BookeoHealth() {
  const { data } = useSWR("/admin/bookeo/status", fetcher, { refreshInterval: 60000 });
  if (!data || !data.enabled) return null;
  const dot = (ok) => `inline-block h-2.5 w-2.5 rounded-full ${ok ? "bg-[#2fd0c6]" : "bg-[#ff5147]"}`;
  const fmt = (s) => (s ? new Date(s).toLocaleString() : "—");
  const healthy = data.connected && data.webhooks_ok;
  return (
    <div className="card p-6 mt-6" data-testid="bookeo-health">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className={dot(healthy)} />
          <h3 className="font-display font-bold text-[#f6ecd4]">Bookeo Sync</h3>
          <span className="text-xs text-[#8fa5a5]">{healthy ? "Connected" : "Attention needed"}</span>
        </div>
        <span className="text-xs text-[#8fa5a5]" data-testid="bookeo-synced-count">{data.synced_bookings} synced booking{data.synced_bookings === 1 ? "" : "s"}</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
        <div className="flex items-center gap-2 text-[#a9bdbd]"><span className={dot(data.connected)} /> API connection</div>
        <div className="flex items-center gap-2 text-[#a9bdbd]"><span className={dot(data.webhooks_ok)} /> Webhooks registered ({data.webhooks?.length || 0}/3)</div>
        <div className="text-[#a9bdbd]"><span className="text-[#8fa5a5]">Last website → Bookeo sync:</span> {fmt(data.last_sync)}</div>
        <div className="text-[#a9bdbd]"><span className="text-[#8fa5a5]">Last Bookeo webhook:</span> {fmt(data.last_webhook)}</div>
      </div>
      {data.permissions?.length > 0 && <div className="text-[10px] text-[#8fa5a5] mt-4">Permissions: {data.permissions.join(", ")}</div>}
    </div>
  );
}

function Reservations() {
  const { data } = useSWR("/admin/reservations", fetcher);
  const [editing, setEditing] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [adding, setAdding] = useState(false);

  const markPaid = async (r) => {
    const check = window.prompt(`Mark ${r.name}'s $${r.amount} as paid by check.\nCheck number (optional):`, "");
    if (check === null) return;
    try { await api.post(`/admin/reservations/${r.id}/mark-paid`, { check_number: check }); toast.success("Marked as paid."); mutate("/admin/reservations"); mutate("/admin/stats"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed."); }
  };

  const syncBookeo = async (r) => {
    setSyncing(r.id);
    try {
      const { data: res } = await api.post(`/admin/reservations/${r.id}/sync-bookeo`);
      if (res.ok) toast.success(`Pushed to Bookeo${res.booking_number ? ` (#${res.booking_number})` : ""}.`);
      else toast.error(res.error || "Bookeo sync failed.");
      mutate("/admin/reservations");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Bookeo sync failed."); }
    finally { setSyncing(null); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this reservation? This cannot be undone.")) return;
    try { await api.delete(`/admin/reservations/${id}`); toast.success("Deleted."); mutate("/admin/reservations"); mutate("/admin/stats"); }
    catch (e) { toast.error("Failed to delete."); }
  };
  const saveEdit = async () => {
    try {
      await api.put(`/admin/reservations/${editing.id}`, {
        reservation_date: editing.reservation_date, start_time: editing.start_time,
        hours: Number(editing.hours), status: editing.status, phone: editing.phone, notes: editing.notes || "",
      });
      toast.success("Reservation updated."); setEditing(null); mutate("/admin/reservations");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed."); }
  };

  const downloadAgreement = async (r) => {
    try {
      const res = await api.get(`/admin/reservations/${r.id}/agreement`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `agreement-${r.name}-${r.reservation_date}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate the PDF."); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <Header title="Reservations" sub="Manage bookings. Edit details or remove a reservation." />
        <button onClick={() => setAdding(true)} className="btn-primary px-5 py-2.5 flex items-center gap-2 mb-6" data-testid="res-add-btn"><Plus size={16} /> New Reservation</button>
      </div>
      {adding && <NewReservationModal onClose={() => setAdding(false)} />}
      <div className="card overflow-x-auto hidden md:block">
        <table className="w-full text-sm" data-testid="reservations-table">
          <thead><tr className="text-left text-[#8fa5a5] border-b border-[#33281c]">
            {["Name", "Date", "Time", "Hrs", "Event", "Total", "Status", "Bookeo", ""].map((h) => <th key={h} className="p-4 font-semibold">{h}</th>)}
          </tr></thead>
          <tbody>
            {(data || []).map((r) => (
              <tr key={r.id} className="border-b border-[#2a2114] hover:bg-[#241a12]">
                <td className="p-4"><div className="font-semibold text-[#f6ecd4]">{r.name}</div><div className="text-[#8fa5a5] text-xs">{r.email}</div></td>
                <td className="p-4 text-[#d9cbb0]">{r.reservation_date}</td>
                <td className="p-4 text-[#d9cbb0]">{r.start_time}</td>
                <td className="p-4 font-mono-arcade text-[#d9cbb0]">{r.hours}</td>
                <td className="p-4 text-[#d9cbb0]">{r.event_type}</td>
                <td className="p-4 font-mono-arcade text-[#d9cbb0]">${r.amount}</td>
                <td className="p-4">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${resBadge(r)[1]}`} data-testid={`res-status-${r.id}`}>{resBadge(r)[0]}</span>
                  {r.check_number && <div className="text-[10px] text-[#8fa5a5] mt-1">Check #{r.check_number}</div>}
                  {r.payment_status === "check_due" && <button onClick={() => markPaid(r)} className="block text-xs font-semibold text-[#2fd0c6] mt-1.5 hover:underline" data-testid={`res-mark-paid-${r.id}`}>Mark as paid</button>}
                </td>
                <td className="p-4">
                  {r.source === "admin" ? <span className="text-[#8fa5a5] text-xs" data-testid={`res-website-only-${r.id}`}>Website only</span> : r.payment_status === "paid" ? (
                    r.bookeo_booking_number ? (
                      <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-[#14241f] text-[#2fd0c6]" data-testid={`res-bookeo-ok-${r.id}`}>In Bookeo #{r.bookeo_booking_number}</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span title={r.bookeo_error || "Not pushed to Bookeo yet"} className="px-2.5 py-1 rounded-full text-xs font-semibold bg-[#2a1a12] text-[#f6b64a]" data-testid={`res-bookeo-missing-${r.id}`}>Not in Bookeo</span>
                        <button onClick={() => syncBookeo(r)} disabled={syncing === r.id} className="text-[#2fd0c6] hover:text-[#f6ecd4] disabled:opacity-50" title="Push to Bookeo" data-testid={`res-sync-${r.id}`}><RefreshCw size={15} className={syncing === r.id ? "animate-spin" : ""} /></button>
                      </div>
                    )
                  ) : <span className="text-[#8fa5a5] text-xs">—</span>}
                </td>
                <td className="p-4 flex gap-2">
                  <button onClick={() => setEditing({ ...r })} className="text-[#2fd0c6] hover:text-[#f6ecd4]" data-testid={`res-edit-${r.id}`}><Pencil size={16} /></button>
                  {r.signature_data && <button onClick={() => downloadAgreement(r)} className="text-[#a9bdbd] hover:text-[#f6ecd4]" title="Download signed agreement PDF" data-testid={`res-pdf-${r.id}`}><Download size={16} /></button>}
                  <button onClick={() => del(r.id)} className="text-[#ff5147] hover:text-[#ff5147]" data-testid={`res-del-${r.id}`}><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
            {data && data.length === 0 && <tr><td colSpan={9} className="p-10 text-center text-[#8fa5a5]">No reservations yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-3" data-testid="reservations-cards">
        {(data || []).map((r) => (
          <div key={r.id} className="card p-4" data-testid={`res-card-${r.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-[#f6ecd4] truncate">{r.name}</div>
                <div className="text-[#8fa5a5] text-xs truncate">{r.email}</div>
              </div>
              <span className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold ${resBadge(r)[1]}`} data-testid={`res-status-m-${r.id}`}>{resBadge(r)[0]}</span>
            </div>
            <div className="grid grid-cols-2 gap-y-1.5 gap-x-3 mt-3 text-sm">
              <div className="text-[#8fa5a5]">Date</div><div className="text-[#d9cbb0] text-right">{r.reservation_date}</div>
              <div className="text-[#8fa5a5]">Time</div><div className="text-[#d9cbb0] text-right">{r.start_time} · {r.hours}h</div>
              <div className="text-[#8fa5a5]">Event</div><div className="text-[#d9cbb0] text-right truncate">{r.event_type}</div>
              <div className="text-[#8fa5a5]">Total</div><div className="font-mono-arcade text-[#d9cbb0] text-right">${r.amount}</div>
              {r.check_number && <><div className="text-[#8fa5a5]">Check</div><div className="text-[#d9cbb0] text-right" data-testid={`res-check-m-${r.id}`}>#{r.check_number}</div></>}
            </div>
            {r.payment_status === "check_due" && <button onClick={() => markPaid(r)} className="mt-3 w-full py-2 rounded-lg bg-[#14241f] border border-[#33281c] text-[#2fd0c6] text-sm font-semibold" data-testid={`res-mark-paid-m-${r.id}`}>Mark as paid (check)</button>}
            {r.payment_status === "paid" && r.source !== "admin" && (
              <div className="mt-3 flex items-center justify-between text-sm" data-testid={`res-bookeo-m-${r.id}`}>
                <span className="text-[#8fa5a5]">Bookeo</span>
                {r.bookeo_booking_number
                  ? <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-[#14241f] text-[#2fd0c6]">In Bookeo #{r.bookeo_booking_number}</span>
                  : <button onClick={() => syncBookeo(r)} disabled={syncing === r.id} className="px-3 py-1.5 rounded-lg bg-[#2a1a12] border border-[#33281c] text-[#f6b64a] text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50" data-testid={`res-sync-m-${r.id}`}><RefreshCw size={13} className={syncing === r.id ? "animate-spin" : ""} /> Push to Bookeo</button>}
              </div>
            )}
            <div className="flex gap-2 mt-3 pt-3 border-t border-[#2a2114]">
              <button onClick={() => setEditing({ ...r })} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-[#221a12] border border-[#33281c] text-[#2fd0c6] text-sm font-semibold" data-testid={`res-edit-m-${r.id}`}><Pencil size={15} /> Edit</button>
              {r.signature_data && <button onClick={() => downloadAgreement(r)} className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-[#221a12] border border-[#33281c] text-[#a9bdbd] text-sm font-semibold" data-testid={`res-pdf-m-${r.id}`}><Download size={15} /> PDF</button>}
              <button onClick={() => del(r.id)} className="flex items-center justify-center py-2 px-3 rounded-lg bg-[#2a1a12] border border-[#33281c] text-[#ff5147]" data-testid={`res-del-m-${r.id}`}><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {data && data.length === 0 && <div className="card p-10 text-center text-[#8fa5a5]">No reservations yet.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm grid place-items-center p-4" onClick={() => setEditing(null)}>
          <div className="card p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="res-edit-modal">
            <div className="flex justify-between items-center mb-4"><h3 className="font-display text-xl font-bold text-[#f6ecd4]">Edit Reservation</h3><button onClick={() => setEditing(null)}><X size={20} className="text-[#8fa5a5]" /></button></div>
            <div className="space-y-3">
              <div><label className="text-xs text-[#8fa5a5]">Date</label><input type="date" className={`${inp} mt-1`} value={editing.reservation_date} onChange={(e) => setEditing({ ...editing, reservation_date: e.target.value })} /></div>
              <div><label className="text-xs text-[#8fa5a5]">Start Time</label><input className={`${inp} mt-1`} value={editing.start_time} onChange={(e) => setEditing({ ...editing, start_time: e.target.value })} /></div>
              <div><label className="text-xs text-[#8fa5a5]">Hours</label><input type="number" className={`${inp} mt-1`} value={editing.hours} onChange={(e) => setEditing({ ...editing, hours: e.target.value })} /></div>
              <div><label className="text-xs text-[#8fa5a5]">Phone</label><input className={`${inp} mt-1`} value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} /></div>
              <div><label className="text-xs text-[#8fa5a5]">Status</label>
                <select className={`${inp} mt-1`} value={editing.status || "pending_payment"} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                  <option value="pending_payment">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div><label className="text-xs text-[#8fa5a5]">Notes</label><textarea rows={2} className={`${inp} mt-1`} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></div>
              {editing.signature_data && (
                <div data-testid="res-signature">
                  <label className="text-xs text-[#8fa5a5]">Signed Agreement - {editing.signer_name || "-"}</label>
                  <img src={editing.signature_data} alt="Signature" className="mt-1 w-full max-h-40 object-contain bg-white rounded-lg border border-[#33281c]" />
                  <p className="text-[10px] text-[#8fa5a5] mt-1">Agreement accepted: {editing.agreement_accepted ? "Yes" : "No"} · DOB: {editing.birthdate || "-"}</p>
                </div>
              )}
            </div>
            {editing.signature_data && <button onClick={() => downloadAgreement(editing)} className="btn-outline w-full py-3 flex items-center justify-center gap-2 mt-4" data-testid="res-pdf-modal"><Download size={16} /> Download Agreement PDF</button>}
            <button onClick={saveEdit} className="btn-primary w-full py-3 flex items-center justify-center gap-2 mt-5" data-testid="res-save-btn"><Save size={16} /> Save Changes</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Contacts() {
  const { data } = useSWR("/admin/contacts", fetcher);
  return (
    <div>
      <Header title="Messages" sub="Contact form submissions." />
      <div className="space-y-4">
        {(data || []).map((m) => (
          <div key={m.id} className="card p-5">
            <div className="flex justify-between"><span className="font-semibold text-[#f6ecd4]">{m.name}</span><span className="text-xs text-[#8fa5a5]">{m.email} • {m.phone}</span></div>
            <p className="text-[#a9bdbd] text-sm mt-2">{m.message}</p>
          </div>
        ))}
        {data && data.length === 0 && <div className="card p-10 text-center text-[#8fa5a5]">No messages yet.</div>}
      </div>
    </div>
  );
}

async function saveSection(section, data) {
  try { await api.put("/admin/content", { section, data }); toast.success("Saved!"); mutate("/content"); }
  catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed."); }
}

function ReviewsEditor() {
  const { data: content } = useSWR("/content", fetcher);
  const [items, setItems] = useState(null);
  useEffect(() => { if (content?.reviews?.items) setItems(content.reviews.items); }, [content]);
  if (!items) return null;
  const upd = (i, k, v) => { const n = [...items]; n[i] = { ...n[i], [k]: v }; setItems(n); };
  return (
    <div className="max-w-3xl">
      <Header title="Reviews" sub="Add, edit, or remove guest reviews shown on the home page." />
      <div className="space-y-4">
        {items.map((r, i) => (
          <div key={i} className="card p-5 space-y-3" data-testid={`review-item-${i}`}>
            <div className="grid sm:grid-cols-2 gap-3">
              <input className={inp} placeholder="Name" value={r.name} onChange={(e) => upd(i, "name", e.target.value)} />
              <input className={inp} placeholder="Role (e.g. Birthday Mom)" value={r.role} onChange={(e) => upd(i, "role", e.target.value)} />
            </div>
            <textarea rows={2} className={inp} placeholder="Review text" value={r.text} onChange={(e) => upd(i, "text", e.target.value)} />
            <button onClick={() => setItems(items.filter((_, x) => x !== i))} className="text-[#ff5147] text-sm flex items-center gap-1" data-testid={`review-del-${i}`}><Trash2 size={14} /> Remove</button>
          </div>
        ))}
      </div>
      <div className="flex gap-3 mt-4">
        <button onClick={() => setItems([...items, { name: "", role: "", text: "" }])} className="btn-outline px-5 py-2.5 flex items-center gap-2" data-testid="review-add"><Plus size={16} /> Add Review</button>
        <button onClick={() => saveSection("reviews", { items })} className="btn-primary px-6 py-2.5 flex items-center gap-2" data-testid="review-save"><Save size={16} /> Save</button>
      </div>
    </div>
  );
}

function PricingEditor() {
  const { data: content } = useSWR("/content", fetcher);
  const [p, setP] = useState(null);
  const [od, setOd] = useState("");
  useEffect(() => { if (content?.pricing) setP(content.pricing); }, [content]);
  useEffect(() => { if (content?.opening_date) setOd(String(content.opening_date).slice(0, 16)); }, [content]);
  if (!p) return null;
  return (
    <div className="max-w-lg">
      <Header title="Pricing & Opening" />
      <div className="card p-6 space-y-4" data-testid="pricing-editor">
        {[["base_hours", "Minimum Hours"], ["base_price", "Base Price ($)"], ["additional_hour_price", "Additional Hour ($)"], ["hero_discount", "HERO Discount ($)"]].map(([k, l]) => (
          <div key={k}><label className="text-sm text-[#a9bdbd]">{l}</label><input type="number" className={`${inp} mt-1`} value={p[k]} onChange={(e) => setP({ ...p, [k]: Number(e.target.value) })} data-testid={`pricing-${k}`} /></div>
        ))}
        <button onClick={() => saveSection("pricing", p)} className="btn-primary px-6 py-3 flex items-center gap-2" data-testid="save-pricing"><Save size={16} /> Save</button>
      </div>
      <div className="card p-6 space-y-4 mt-6" data-testid="opening-editor">
        <div>
          <label className="text-sm text-[#a9bdbd]">Grand Opening Date & Time</label>
          <input type="datetime-local" className={`${inp} mt-1`} style={{ colorScheme: "dark" }} value={od} onChange={(e) => setOd(e.target.value)} data-testid="opening-date-input" />
          <p className="text-xs text-[#8fa5a5] mt-1">Drives the homepage countdown and the "Grand Opening" label. Once this moment passes, the hero flips to "NOW OPEN".</p>
        </div>
        <button onClick={() => saveSection("opening_date", od)} className="btn-primary px-6 py-3 flex items-center gap-2" data-testid="save-opening"><Save size={16} /> Save Opening Date</button>
      </div>
    </div>
  );
}

function HoursEditor() {
  const { data: content } = useSWR("/content", fetcher);
  const [hrs, setHrs] = useState(null);
  useEffect(() => { if (content?.hours) setHrs(content.hours); }, [content]);
  if (!hrs) return null;
  return (
    <div className="max-w-lg">
      <Header title="Operating Hours" />
      <div className="card p-6 space-y-3">
        {hrs.map((h, i) => (
          <div key={h.day} className="flex items-center gap-3">
            <span className="w-28 text-sm text-[#a9bdbd]">{h.day}</span>
            <input className={inp} value={h.hours} onChange={(e) => { const n = [...hrs]; n[i] = { ...h, hours: e.target.value }; setHrs(n); }} />
          </div>
        ))}
        <button onClick={() => saveSection("hours", hrs)} className="btn-primary px-6 py-3 flex items-center gap-2"><Save size={16} /> Save</button>
      </div>
    </div>
  );
}

function AgreementEditor() {
  const { data: content } = useSWR("/content", fetcher);
  const [a, setA] = useState(null);
  useEffect(() => { if (content?.agreement) setA(content.agreement); }, [content]);
  if (!a) return null;
  return (
    <div className="max-w-3xl">
      <Header title="Reservation Agreement" sub="Edit the legal text guests sign." />
      <div className="card p-6 space-y-4">
        <input className={inp} value={a.title} onChange={(e) => setA({ ...a, title: e.target.value })} />
        <textarea rows={16} className={inp} value={a.body} onChange={(e) => setA({ ...a, body: e.target.value })} data-testid="agreement-editor" />
        <button onClick={() => saveSection("agreement", a)} className="btn-primary px-6 py-3 flex items-center gap-2"><Save size={16} /> Save</button>
      </div>
    </div>
  );
}

function PolicyEditor({ section, title, defaults }) {
  const { data: content } = useSWR("/content", fetcher);
  const [a, setA] = useState(null);
  useEffect(() => { if (content) setA(content[section]?.body ? content[section] : defaults); }, [content, section, defaults]);
  if (!a) return null;
  return (
    <div className="max-w-3xl" data-testid={`policy-editor-${section}`}>
      <Header title={title} sub="Shown on the public Privacy & Policies page. {email} is replaced with your contact email." />
      <div className="card p-6 space-y-4">
        <input aria-label={`${title} heading`} className={inp} value={a.title} onChange={(e) => setA({ ...a, title: e.target.value })} data-testid={`policy-title-${section}`} />
        <textarea aria-label={`${title} text`} rows={section === "privacy" ? 18 : 6} className={inp} value={a.body} onChange={(e) => setA({ ...a, body: e.target.value })} data-testid={`policy-body-${section}`} />
        <button onClick={() => saveSection(section, a)} className="btn-primary px-6 py-3 flex items-center gap-2" data-testid={`policy-save-${section}`}><Save size={16} /> Save</button>
      </div>
    </div>
  );
}

function DiscountEditor() {
  const { data: content } = useSWR("/content", fetcher);
  const [d, setD] = useState(null);
  useEffect(() => { if (content?.discount) setD(content.discount); }, [content]);
  if (!d) return null;
  return (
    <div className="max-w-2xl">
      <Header title="HERO Discount" />
      <div className="card p-6 space-y-4">
        <div><label className="text-sm text-[#a9bdbd]">Title</label><input className={`${inp} mt-1`} value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} /></div>
        <div><label className="text-sm text-[#a9bdbd]">Amount ($)</label><input type="number" className={`${inp} mt-1`} value={d.amount} onChange={(e) => setD({ ...d, amount: Number(e.target.value) })} /></div>
        <div><label className="text-sm text-[#a9bdbd]">Description</label><textarea rows={2} className={`${inp} mt-1`} value={d.description} onChange={(e) => setD({ ...d, description: e.target.value })} /></div>
        <div><label className="text-sm text-[#a9bdbd]">Terms</label><textarea rows={2} className={`${inp} mt-1`} value={d.terms} onChange={(e) => setD({ ...d, terms: e.target.value })} /></div>
        <div><label className="text-sm text-[#a9bdbd]">Eligible Groups (comma separated)</label><input className={`${inp} mt-1`} value={d.eligible.join(", ")} onChange={(e) => setD({ ...d, eligible: e.target.value.split(",").map((s) => s.trim()) })} /></div>
        <button onClick={() => saveSection("discount", d)} className="btn-primary px-6 py-3 flex items-center gap-2"><Save size={16} /> Save</button>
      </div>
    </div>
  );
}

function ContactEditor() {
  const { data: content } = useSWR("/content", fetcher);
  const [c, setC] = useState(null);
  useEffect(() => { if (content?.contact) setC(content.contact); }, [content]);
  if (!c) return null;
  return (
    <div className="max-w-lg">
      <Header title="Contact Info" />
      <div className="card p-6 space-y-4">
        {[["phone", "Phone"], ["email", "Email"], ["address", "Address"], ["facebook", "Facebook URL"], ["map_embed", "Google Map Embed URL"]].map(([k, l]) => (
          <div key={k}><label className="text-sm text-[#a9bdbd]">{l}</label><input className={`${inp} mt-1`} value={c[k]} onChange={(e) => setC({ ...c, [k]: e.target.value })} data-testid={`contact-${k}`} /></div>
        ))}
        <button onClick={() => saveSection("contact", c)} className="btn-primary px-6 py-3 flex items-center gap-2"><Save size={16} /> Save</button>
      </div>
    </div>
  );
}

function BlackoutEditor() {
  const TIME_SLOTS = ["10:00 AM", "12:30 PM", "1:00 PM", "3:00 PM", "3:30 PM", "5:30 PM", "6:00 PM"];
  const { data: content } = useSWR("/content", fetcher);
  const [items, setItems] = useState(null);
  useEffect(() => { if (content?.blackouts?.items) setItems(content.blackouts.items); }, [content]);
  if (!items) return null;
  const upd = (i, k, v) => { const n = [...items]; n[i] = { ...n[i], [k]: v }; setItems(n); };
  return (
    <div className="max-w-2xl">
      <Header title="Closed Dates" sub="Block a whole day or a specific time slot so no one can book then." />
      <div className="space-y-3">
        {items.map((b, i) => (
          <div key={i} className="card p-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center" data-testid={`blackout-item-${i}`}>
            <input type="date" className={`${inp} sm:w-44`} value={b.date || ""} onChange={(e) => upd(i, "date", e.target.value)} data-testid={`blackout-date-${i}`} />
            <select className={`${inp} sm:w-44`} value={b.time || ""} onChange={(e) => upd(i, "time", e.target.value)} data-testid={`blackout-time-${i}`}>
              <option value="">Whole Day</option>
              {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <button onClick={() => setItems(items.filter((_, x) => x !== i))} className="text-[#ff5147] flex items-center gap-1 text-sm" data-testid={`blackout-del-${i}`}><Trash2 size={15} /> Remove</button>
          </div>
        ))}
        {items.length === 0 && <div className="card p-8 text-center text-[#8fa5a5]">No closed dates yet. Add one below.</div>}
      </div>
      <div className="flex gap-3 mt-4">
        <button onClick={() => setItems([...items, { date: "", time: "" }])} className="btn-outline px-5 py-2.5 flex items-center gap-2" data-testid="blackout-add"><Plus size={16} /> Add Closed Date</button>
        <button onClick={() => saveSection("blackouts", { items: items.filter((b) => b.date) })} className="btn-primary px-6 py-2.5 flex items-center gap-2" data-testid="blackout-save"><Save size={16} /> Save</button>
      </div>
    </div>
  );
}

function CalendarTab() {
  const { data } = useSWR("/admin/reservations", fetcher);
  const now = new Date();
  const [cursor, setCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [selected, setSelected] = useState(null);

  const byDate = {};
  (data || []).forEach((r) => { (byDate[r.reservation_date] = byDate[r.reservation_date] || []).push(r); });

  const first = new Date(cursor.y, cursor.m, 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells = [...Array(startDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const fmt = (d) => `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const monthName = first.toLocaleString("default", { month: "long", year: "numeric" });
  const shift = (delta) => { let m = cursor.m + delta, y = cursor.y; if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; } setCursor({ y, m }); setSelected(null); };
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const dow = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div>
      <Header title="Booking Calendar" sub="Days with a colored dot have reservations. Click a day to see the details." />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => shift(-1)} className="p-2 rounded-lg hover:bg-white/5 text-[#a9bdbd]" data-testid="cal-prev"><ChevronLeft size={20} /></button>
            <h3 className="font-display text-xl font-bold text-white" data-testid="cal-month">{monthName}</h3>
            <button onClick={() => shift(1)} className="p-2 rounded-lg hover:bg-white/5 text-[#a9bdbd]" data-testid="cal-next"><ChevronRight size={20} /></button>
          </div>
          <div className="grid grid-cols-7 gap-1.5 text-center text-xs text-[#8fa5a5] mb-2">
            {dow.map((d) => <div key={d}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {cells.map((d, i) => {
              if (!d) return <div key={i} />;
              const key = fmt(d);
              const items = byDate[key] || [];
              const has = items.length > 0;
              const paid = items.some((r) => r.payment_status === "paid" || r.status === "confirmed");
              const isSel = selected === key;
              return (
                <button key={i} onClick={() => setSelected(key)} data-testid={`cal-day-${d}`}
                  className={`aspect-square rounded-lg text-sm font-semibold flex flex-col items-center justify-center transition border ${isSel ? "border-[#ff5147] bg-[#ff5147]/15" : "border-transparent hover:bg-white/5"} ${key === todayStr ? "ring-1 ring-[#2fd0c6]" : ""} text-white`}>
                  {d}
                  {has && <span className={`mt-1 h-1.5 w-1.5 rounded-full ${paid ? "bg-[#2fd0c6]" : "bg-[#f6b64a]"}`} />}
                </button>
              );
            })}
          </div>
          <div className="flex gap-4 mt-4 text-xs text-[#a9bdbd]">
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#2fd0c6]" /> Confirmed / Paid</span>
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#f6b64a]" /> Pending</span>
          </div>
        </div>

        <div className="card p-5" data-testid="cal-day-detail">
          <h4 className="font-display font-bold text-white mb-3">{selected || "Select a day"}</h4>
          {selected && (byDate[selected] || []).length === 0 && <p className="text-[#8fa5a5] text-sm">No reservations on this day.</p>}
          <div className="space-y-3">
            {(byDate[selected] || []).sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")).map((r) => (
              <div key={r.id} className="rounded-xl border border-[#33281c] p-3">
                <div className="flex justify-between items-center">
                  <span className="font-mono-arcade text-[#2fd0c6]">{r.start_time}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${r.payment_status === "paid" || r.status === "confirmed" ? "bg-[#2fd0c6]/15 text-[#2fd0c6]" : "bg-white/10 text-[#a9bdbd]"}`}>{r.payment_status === "paid" || r.status === "confirmed" ? "Confirmed" : "Pending"}</span>
                </div>
                <div className="text-sm text-white mt-1">{r.name} · {r.hours}h · {r.event_type}</div>
                <div className="text-xs text-[#8fa5a5]">{r.email}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function GiftCardsAdmin() {
  const { data } = useSWR("/admin/giftcards", fetcher);
  const { data: content } = useSWR("/content", fetcher);
  const enabled = content?.settings?.gift_cards_enabled;
  const [issue, setIssue] = useState({ amount: "", recipient_name: "", note: "" });
  const issueCard = async () => {
    const amt = Number(issue.amount);
    if (!amt || amt <= 0) { toast.error("Enter a dollar amount."); return; }
    try {
      const { data: r } = await api.post("/admin/giftcards", { amount: amt, recipient_name: issue.recipient_name, note: issue.note });
      toast.success(`Gift card ${r.code} issued for $${r.balance}.`);
      setIssue({ amount: "", recipient_name: "", note: "" });
      mutate("/admin/giftcards");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to issue."); }
  };
  const toggleSales = async () => {
    try { await api.put("/admin/content", { section: "settings", data: { gift_cards_enabled: !enabled } }); toast.success(!enabled ? "Gift card sales turned ON." : "Gift card sales turned OFF."); mutate("/content"); }
    catch { toast.error("Failed to update."); }
  };
  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/giftcards/${id}`, { status }); toast.success("Updated."); mutate("/admin/giftcards"); }
    catch { toast.error("Failed to update."); }
  };
  const badge = (g) => g.payment_status !== "paid" ? "bg-[#2a2114] text-[#8fa5a5]"
    : g.status === "active" ? "bg-[#14241f] text-[#2fd0c6]"
    : g.status === "redeemed" ? "bg-[#2a2114] text-[#8fa5a5]"
    : "bg-[#2a1a12] text-[#ff5147]";
  const label = (g) => g.payment_status !== "paid" ? "Unpaid" : g.status === "active" ? "Active" : g.status === "redeemed" ? "Redeemed" : "Void";
  return (
    <div>
      <Header title="Gift Cards" sub="Every purchased gift card, its remaining balance, and controls to void or reactivate." />
      <div className="card p-4 mb-6 flex items-center justify-between" data-testid="giftcard-toggle-row">
        <div><div className="font-semibold text-[#f6ecd4]">Gift Card Sales</div><div className="text-xs text-[#8fa5a5]">{enabled ? "Customers can buy gift cards on the site." : "Buying is hidden from the public site."}</div></div>
        <button onClick={toggleSales} data-testid="giftcard-sales-toggle" className={`px-6 py-2.5 rounded-full font-mono-arcade text-sm font-bold border-2 ${enabled ? "bg-[#14241f] text-[#2fd0c6] border-[#2fd0c6]" : "bg-[#2a1a12] text-[#8fa5a5] border-[#33281c]"}`}>{enabled ? "ON" : "OFF"}</button>
      </div>
      <div className="card p-5 mb-6" data-testid="giftcard-issue">
        <div className="font-semibold text-[#f6ecd4] mb-1">Issue a Gift Card Manually</div>
        <div className="text-xs text-[#8fa5a5] mb-4">Creates an active card instantly (no payment). Hand the code to a customer — it works like any purchased card.</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div><label className="text-xs text-[#8fa5a5]">Amount ($)</label><input type="number" min="1" className={`${inp} mt-1`} placeholder="50" value={issue.amount} onChange={(e) => setIssue({ ...issue, amount: e.target.value })} data-testid="issue-gift-amount" /></div>
          <div><label className="text-xs text-[#8fa5a5]">Recipient name (optional)</label><input className={`${inp} mt-1`} value={issue.recipient_name} onChange={(e) => setIssue({ ...issue, recipient_name: e.target.value })} data-testid="issue-gift-recipient" /></div>
          <div><label className="text-xs text-[#8fa5a5]">Note (optional)</label><input className={`${inp} mt-1`} value={issue.note} onChange={(e) => setIssue({ ...issue, note: e.target.value })} data-testid="issue-gift-note" /></div>
        </div>
        <button onClick={issueCard} className="btn-primary px-6 py-2.5 flex items-center gap-2 mt-4" data-testid="issue-gift-btn"><Plus size={16} /> Issue Gift Card</button>
      </div>
      <div className="card overflow-x-auto hidden md:block">
        <table className="w-full text-sm" data-testid="giftcards-table">
          <thead><tr className="text-left text-[#8fa5a5] border-b border-[#33281c]">
            {["Code", "Amount", "Balance", "Buyer", "Recipient", "Status", ""].map((h) => <th key={h} className="p-4 font-semibold">{h}</th>)}
          </tr></thead>
          <tbody>
            {(data || []).map((g) => (
              <tr key={g.id} className="border-b border-[#2a2114] hover:bg-[#241a12]">
                <td className="p-4 font-mono-arcade text-[#f6ecd4]">{g.code}</td>
                <td className="p-4 text-[#d9cbb0]">${g.initial_amount ?? g.amount}</td>
                <td className="p-4 font-mono-arcade text-[#2fd0c6]">${g.balance}</td>
                <td className="p-4"><div className="text-[#f6ecd4]">{g.buyer_name}</div><div className="text-xs text-[#8fa5a5]">{g.buyer_email}</div></td>
                <td className="p-4">{g.send_to_recipient ? (<><div className="text-[#f6ecd4]">{g.recipient_name}</div><div className="text-xs text-[#8fa5a5]">{g.recipient_email}</div></>) : <span className="text-[#8fa5a5] text-xs">Buyer keeps it</span>}</td>
                <td className="p-4"><span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${badge(g)}`}>{label(g)}</span></td>
                <td className="p-4">
                  {g.payment_status === "paid" && g.status !== "void" && <button onClick={() => setStatus(g.id, "void")} className="text-[#ff5147] text-xs font-semibold" data-testid={`gift-void-${g.id}`}>Void</button>}
                  {g.status === "void" && <button onClick={() => setStatus(g.id, "active")} className="text-[#2fd0c6] text-xs font-semibold" data-testid={`gift-reactivate-${g.id}`}>Reactivate</button>}
                </td>
              </tr>
            ))}
            {data && data.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-[#8fa5a5]">No gift cards sold yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-3" data-testid="giftcards-cards">
        {(data || []).map((g) => (
          <div key={g.id} className="card p-4" data-testid={`gift-card-${g.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="font-mono-arcade text-[#f6ecd4] break-all">{g.code}</div>
              <span className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold ${badge(g)}`}>{label(g)}</span>
            </div>
            <div className="grid grid-cols-2 gap-y-1.5 gap-x-3 mt-3 text-sm">
              <div className="text-[#8fa5a5]">Amount</div><div className="text-[#d9cbb0] text-right">${g.initial_amount ?? g.amount}</div>
              <div className="text-[#8fa5a5]">Balance</div><div className="font-mono-arcade text-[#2fd0c6] text-right">${g.balance}</div>
              <div className="text-[#8fa5a5]">Buyer</div><div className="text-[#d9cbb0] text-right truncate">{g.buyer_name}</div>
              <div className="text-[#8fa5a5]">Recipient</div><div className="text-[#d9cbb0] text-right truncate">{g.send_to_recipient ? g.recipient_name : "Buyer keeps it"}</div>
            </div>
            {g.payment_status === "paid" && (
              <div className="mt-3 pt-3 border-t border-[#2a2114]">
                {g.status !== "void" && <button onClick={() => setStatus(g.id, "void")} className="w-full py-2 rounded-lg bg-[#2a1a12] border border-[#33281c] text-[#ff5147] text-sm font-semibold" data-testid={`gift-void-m-${g.id}`}>Void</button>}
                {g.status === "void" && <button onClick={() => setStatus(g.id, "active")} className="w-full py-2 rounded-lg bg-[#14241f] border border-[#33281c] text-[#2fd0c6] text-sm font-semibold" data-testid={`gift-reactivate-m-${g.id}`}>Reactivate</button>}
              </div>
            )}
          </div>
        ))}
        {data && data.length === 0 && <div className="card p-10 text-center text-[#8fa5a5]">No gift cards sold yet.</div>}
      </div>
    </div>
  );
}

function WaiversAdmin() {
  const { data } = useSWR("/admin/waivers", fetcher);
  const { data: content } = useSWR("/content", fetcher);
  const [copied, setCopied] = useState(false);
  const kioskUrl = `${window.location.origin}/waiver`;
  const ownerCopy = !!content?.settings?.waiver_owner_copy;

  const toggleOwnerCopy = async () => {
    try { await api.put("/admin/content", { section: "settings", data: { waiver_owner_copy: !ownerCopy } }); toast.success(!ownerCopy ? "You'll now get a copy of each signed waiver." : "Owner copies turned off."); mutate("/content"); }
    catch { toast.error("Failed to update."); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(kioskUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Could not copy the link."); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this signed waiver? This cannot be undone.")) return;
    try { await api.delete(`/admin/waivers/${id}`); toast.success("Deleted."); mutate("/admin/waivers"); }
    catch { toast.error("Failed to delete."); }
  };
  const downloadPdf = async (w) => {
    try {
      const res = await api.get(`/admin/waivers/${w.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `waiver-${w.participant_name}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate the PDF."); }
  };
  const fmt = (s) => (s ? String(s).slice(0, 10) : "-");

  return (
    <div>
      <Header title="Waivers" sub="Signed liability waivers. Post the kiosk link or QR at your front desk so each parent signs for their child." />

      <div className="card p-4 mb-6 flex items-center justify-between" data-testid="waiver-owner-copy-row">
        <div><div className="font-semibold text-[#f6ecd4]">Email me a copy of each signed waiver</div><div className="text-xs text-[#8fa5a5]">{ownerCopy ? "A signed PDF is sent to your contact email every time someone signs." : "Owner copies are off. Families still get their own copy if they enter an email."}</div></div>
        <button onClick={toggleOwnerCopy} data-testid="waiver-owner-copy-toggle" className={`px-6 py-2.5 rounded-full font-mono-arcade text-sm font-bold border-2 ${ownerCopy ? "bg-[#14241f] text-[#2fd0c6] border-[#2fd0c6]" : "bg-[#2a1a12] text-[#8fa5a5] border-[#33281c]"}`}>{ownerCopy ? "ON" : "OFF"}</button>
      </div>

      <div className="card p-5 mb-6 flex flex-col sm:flex-row gap-5 items-start" data-testid="waiver-kiosk-card">
        <div className="bg-white p-3 rounded-xl shrink-0"><QRCodeCanvas value={kioskUrl} size={132} data-testid="waiver-qr" /></div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[#f6ecd4] font-semibold mb-1"><QrCode size={18} className="text-[#ff5147]" /> Kiosk / Sign-In Link</div>
          <div className="text-xs text-[#8fa5a5] mb-3">Families scan this code or open the link on any phone to sign the waiver. Great for a tablet at the front desk.</div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input readOnly value={kioskUrl} className={`${inp} font-mono-arcade text-sm`} data-testid="waiver-kiosk-url" onFocus={(e) => e.target.select()} />
            <button onClick={copy} className="btn-primary px-5 py-2.5 flex items-center justify-center gap-2 shrink-0" data-testid="waiver-copy-link"><Copy size={15} /> {copied ? "Copied!" : "Copy"}</button>
          </div>
        </div>
      </div>

      <div className="card overflow-x-auto hidden md:block">
        <table className="w-full text-sm" data-testid="waivers-table">
          <thead><tr className="text-left text-[#8fa5a5] border-b border-[#33281c]">
            {["Participant", "DOB", "Under 18", "Signed By", "Signed On", ""].map((h) => <th key={h} className="p-4 font-semibold">{h}</th>)}
          </tr></thead>
          <tbody>
            {(data || []).map((w) => (
              <tr key={w.id} className="border-b border-[#2a2114] hover:bg-[#241a12]">
                <td className="p-4 font-semibold text-[#f6ecd4]">{w.participant_name}</td>
                <td className="p-4 text-[#d9cbb0]">{fmt(w.participant_dob)}</td>
                <td className="p-4">{w.is_minor ? <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-[#2a2810] text-[#e0b52e]">Minor</span> : <span className="text-[#8fa5a5] text-xs">Adult</span>}</td>
                <td className="p-4"><div className="text-[#d9cbb0]">{w.signer_name}</div>{w.is_minor && <div className="text-xs text-[#8fa5a5]">Guardian: {w.guardian_name}</div>}</td>
                <td className="p-4 text-[#d9cbb0]">{fmt(w.created_at)}</td>
                <td className="p-4 flex gap-2">
                  <button onClick={() => downloadPdf(w)} className="text-[#a9bdbd] hover:text-[#f6ecd4]" title="Download signed waiver PDF" data-testid={`waiver-pdf-${w.id}`}><Download size={16} /></button>
                  <button onClick={() => del(w.id)} className="text-[#ff5147]" data-testid={`waiver-del-${w.id}`}><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
            {data && data.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-[#8fa5a5]">No waivers signed yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-3" data-testid="waivers-cards">
        {(data || []).map((w) => (
          <div key={w.id} className="card p-4" data-testid={`waiver-card-${w.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0"><div className="font-semibold text-[#f6ecd4] truncate">{w.participant_name}</div><div className="text-xs text-[#8fa5a5]">Signed {fmt(w.created_at)}</div></div>
              {w.is_minor ? <span className="shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold bg-[#2a2810] text-[#e0b52e]">Minor</span> : <span className="shrink-0 text-[#8fa5a5] text-xs">Adult</span>}
            </div>
            <div className="grid grid-cols-2 gap-y-1.5 gap-x-3 mt-3 text-sm">
              <div className="text-[#8fa5a5]">DOB</div><div className="text-[#d9cbb0] text-right">{fmt(w.participant_dob)}</div>
              <div className="text-[#8fa5a5]">Signed by</div><div className="text-[#d9cbb0] text-right truncate">{w.signer_name}</div>
              {w.is_minor && (<><div className="text-[#8fa5a5]">Guardian</div><div className="text-[#d9cbb0] text-right truncate">{w.guardian_name}</div></>)}
            </div>
            <div className="flex gap-2 mt-3 pt-3 border-t border-[#2a2114]">
              <button onClick={() => downloadPdf(w)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-[#221a12] border border-[#33281c] text-[#a9bdbd] text-sm font-semibold" data-testid={`waiver-pdf-m-${w.id}`}><Download size={15} /> PDF</button>
              <button onClick={() => del(w.id)} className="flex items-center justify-center py-2 px-3 rounded-lg bg-[#2a1a12] border border-[#33281c] text-[#ff5147]" data-testid={`waiver-del-m-${w.id}`}><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {data && data.length === 0 && <div className="card p-10 text-center text-[#8fa5a5]">No waivers signed yet.</div>}
      </div>
    </div>
  );
}

function TelegramNotifications() {
  const { data: status, mutate: reload } = useSWR("/admin/telegram/status", fetcher);
  const [detected, setDetected] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const recipients = status?.recipients || [];
  const tokenSet = status?.token_set;

  const detect = async () => {
    setDetecting(true);
    try {
      const { data } = await api.get("/admin/telegram/detect");
      setDetected(data.chats || []);
      if (!data.chats?.length) toast.info("No one has messaged the bot yet. Open the bot and tap Start, then try again.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not detect recipients."); }
    finally { setDetecting(false); }
  };
  const save = async (list) => {
    try { await api.put("/admin/telegram/recipients", { recipients: list }); toast.success("Recipients saved."); reload(); }
    catch { toast.error("Failed to save."); }
  };
  const addChat = (chat) => {
    if (recipients.some((r) => r.id === chat.id)) { toast.info("Already added."); return; }
    save([...recipients, chat]);
  };
  const removeChat = (id) => save(recipients.filter((r) => r.id !== id));
  const test = async () => {
    try { const { data } = await api.post("/admin/telegram/test"); toast.success(`Test sent to ${data.sent_to} recipient(s) — check your phone!`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not send test."); }
  };

  return (
    <div>
      <Header title="Text Notifications" sub="Get an instant Telegram message whenever a booking is paid. Free, no per-text cost." />

      <div className={`card p-4 mb-6 flex items-center gap-3 ${tokenSet ? "" : "border-2 border-[rgba(246,182,74,0.4)]"}`} data-testid="telegram-status">
        <div className={`h-3 w-3 rounded-full ${tokenSet ? "bg-[#2fd0c6]" : "bg-[#f6b64a]"}`} />
        <div className="text-sm">
          <div className="font-semibold text-[#f6ecd4]">{tokenSet ? "Bot connected" : "Bot not connected yet"}</div>
          <div className="text-xs text-[#8fa5a5]">{tokenSet ? "Your Telegram bot is set up. Add recipients below." : "The developer needs to add your Telegram bot token before alerts can send."}</div>
        </div>
      </div>

      <div className="card p-5 mb-6" data-testid="telegram-setup">
        <div className="font-semibold text-[#f6ecd4] mb-2">How to receive alerts</div>
        <ol className="text-sm text-[#a9bdbd] space-y-1.5 list-decimal pl-5">
          <li>Install the free <span className="text-[#f6ecd4] font-semibold">Telegram</span> app on your phone.</li>
          <li>Open your booking bot and tap <span className="text-[#f6ecd4] font-semibold">Start</span> (send it any message).</li>
          <li>Come back here and tap <span className="text-[#f6ecd4] font-semibold">Detect recipients</span> below, then add yourself.</li>
          <li>Tap <span className="text-[#f6ecd4] font-semibold">Send test</span> to confirm it works.</li>
        </ol>
      </div>

      <div className="card p-5 mb-6" data-testid="telegram-recipients">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold text-[#f6ecd4]">Recipients</div>
          <button onClick={test} className="btn-outline px-4 py-2 text-sm flex items-center gap-2" data-testid="telegram-test-btn"><Send size={14} /> Send test</button>
        </div>
        {recipients.length === 0 && <div className="text-sm text-[#8fa5a5] mb-3">No recipients yet. Detect and add below.</div>}
        <div className="space-y-2">
          {recipients.map((r) => (
            <div key={r.id} className="flex items-center justify-between bg-[#221a12] border border-[#33281c] rounded-xl px-4 py-2.5" data-testid={`telegram-recipient-${r.id}`}>
              <div><span className="text-[#f6ecd4] font-semibold">{r.name || "Recipient"}</span> <span className="text-xs text-[#8fa5a5] font-mono-arcade ml-2">{r.id}</span></div>
              <button onClick={() => removeChat(r.id)} className="text-[#ff5147]" data-testid={`telegram-remove-${r.id}`}><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-[#33281c]">
          <button onClick={detect} disabled={detecting} className="btn-primary px-5 py-2.5 flex items-center gap-2 disabled:opacity-60" data-testid="telegram-detect-btn"><Bell size={16} /> {detecting ? "Detecting…" : "Detect recipients"}</button>
          {detected && (
            <div className="mt-3 space-y-2" data-testid="telegram-detected">
              {detected.length === 0 && <div className="text-sm text-[#8fa5a5]">Nobody found. Make sure you messaged the bot first.</div>}
              {detected.map((chat) => (
                <div key={chat.id} className="flex items-center justify-between bg-[#1a140e] border border-[#33281c] rounded-xl px-4 py-2.5">
                  <div><span className="text-[#f6ecd4] font-semibold">{chat.name}</span> <span className="text-xs text-[#8fa5a5] font-mono-arcade ml-2">{chat.id}</span></div>
                  <button onClick={() => addChat(chat)} className="text-[#2fd0c6] text-sm font-semibold flex items-center gap-1" data-testid={`telegram-add-${chat.id}`}><Plus size={14} /> Add</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function GalleryEditor() {
  const { data } = useSWR("/gallery", fetcher);
  const { data: content } = useSWR("/content", fetcher);
  const galleryEnabled = content?.settings?.gallery_enabled !== false;
  const [url, setUrl] = useState(""); const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const optimizeExisting = async () => {
    setOptimizing(true);
    try {
      const { data: res } = await api.post("/admin/gallery/optimize");
      toast.success(`${res.optimized} photo${res.optimized === 1 ? "" : "s"} optimized for fast loading.`);
      mutate("/gallery");
    } catch { toast.error("Could not optimize photos. Please try again."); }
    finally { setOptimizing(false); }
  };
  const toggleGallery = async () => {
    try { await api.put("/admin/content", { section: "settings", data: { gallery_enabled: !galleryEnabled } }); toast.success(!galleryEnabled ? "Gallery page is now visible." : "Gallery page is now hidden."); mutate("/content"); }
    catch { toast.error("Failed to update."); }
  };
  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    setProgress({ done: 0, total: files.length });
    let ok = 0; const errs = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("caption", caption || "");
      try { await api.post("/admin/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); ok++; }
      catch (err) { errs.push(`${f.name}: ${formatApiError(err.response?.data?.detail) || "failed"}`); }
      setProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    setUploading(false);
    setProgress({ done: 0, total: 0 });
    mutate("/gallery");
    if (ok) { toast.success(`${ok} photo${ok === 1 ? "" : "s"} uploaded!`); setCaption(""); }
    if (errs.length) toast.error(`${errs.length} upload${errs.length === 1 ? "" : "s"} failed. ${errs[0]}`);
  };
  const add = async () => {
    if (!url) return;
    try { await api.post("/admin/gallery", { url, caption, order: (data?.length || 0) + 1 }); toast.success("Photo added!"); setUrl(""); setCaption(""); mutate("/gallery"); }
    catch { toast.error("Failed to add."); }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/gallery/${id}`); toast.success("Removed."); mutate("/gallery"); } catch { toast.error("Failed."); }
  };
  return (
    <div>
      <Header title="Gallery" sub="Add or remove photos (paste image URLs)." />
      <div className="card p-4 mb-6 flex items-center justify-between" data-testid="gallery-toggle-row">
        <div><div className="font-semibold text-[#f6ecd4]">Gallery Page</div><div className="text-xs text-[#8fa5a5]">{galleryEnabled ? "Visible in the site menu to everyone." : "Hidden from the menu and blocked to visitors."}</div></div>
        <button onClick={toggleGallery} data-testid="gallery-visibility-toggle" className={`px-6 py-2.5 rounded-full font-mono-arcade text-sm font-bold border-2 ${galleryEnabled ? "bg-[#14241f] text-[#2fd0c6] border-[#2fd0c6]" : "bg-[#2a1a12] text-[#8fa5a5] border-[#33281c]"}`}>{galleryEnabled ? "ON" : "OFF"}</button>
      </div>
      <div className="card p-6 mb-6" data-testid="gallery-add">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <label className={`btn-primary px-6 py-2.5 inline-flex items-center gap-2 shrink-0 cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="gallery-upload-btn">
            <Upload size={16} /> {uploading ? `Uploading ${progress.done}/${progress.total}…` : "Upload Photos"}
            <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} data-testid="gallery-upload-input" />
          </label>
          <span className="text-xs text-[#8fa5a5]">Select one or many photos (up to 50MB each). Auto-compressed for fast loading. Stored permanently — safe across redeploys.</span>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-4 pt-4 border-t border-[#33281c]">
          <button onClick={optimizeExisting} disabled={optimizing} className="px-6 py-2.5 rounded-full border border-[#33281c] text-[#2fd0c6] font-semibold flex items-center gap-2 shrink-0 hover:border-[#2fd0c6] transition disabled:opacity-60" data-testid="gallery-optimize-btn"><Upload size={16} className="rotate-180" /> {optimizing ? "Optimizing…" : "Speed up existing photos"}</button>
          <span className="text-xs text-[#8fa5a5]">Run once to compress photos already uploaded so they load quickly for visitors.</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-4 pt-4 border-t border-[#33281c]">
          <input className={inp} placeholder="…or paste an image URL" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="gallery-url" />
          <input className={inp} placeholder="Caption (optional)" value={caption} onChange={(e) => setCaption(e.target.value)} />
          <button onClick={add} className="px-6 py-2.5 rounded-full border border-[#33281c] text-[#a9bdbd] font-semibold flex items-center gap-2 shrink-0 hover:border-[#ff5147] transition" data-testid="gallery-add-btn"><Plus size={16} /> Add URL</button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {(data || []).map((img) => (
          <div key={img.id} className="relative group rounded-xl overflow-hidden border border-[#33281c]">
            <img src={img.thumb_url || img.url} alt={img.caption} loading="lazy" decoding="async" className="w-full h-32 object-cover" />
            <button onClick={() => del(img.id)} className="absolute top-2 right-2 bg-[#ff5147] text-white p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition" data-testid={`gallery-del-${img.id}`}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function DiscountCodesEditor() {
  const { data } = useSWR("/admin/discounts", fetcher);
  const [f, setF] = useState({ code: "", type: "percent", amount: "", expires_at: "", max_uses: "" });
  const create = async () => {
    const amt = Number(f.amount);
    if (!f.code.trim() || !amt || amt <= 0) { toast.error("Enter a code and an amount."); return; }
    if (f.type === "percent" && amt > 100) { toast.error("Percentage cannot exceed 100."); return; }
    try {
      await api.post("/admin/discounts", {
        code: f.code.trim(), type: f.type, amount: amt, active: true,
        expires_at: f.expires_at || null, max_uses: f.max_uses ? Number(f.max_uses) : null,
      });
      toast.success("Promo code created.");
      setF({ code: "", type: "percent", amount: "", expires_at: "", max_uses: "" });
      mutate("/admin/discounts");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to create."); }
  };
  const toggle = async (d) => {
    try { await api.patch(`/admin/discounts/${d.id}`, { active: !d.active }); mutate("/admin/discounts"); }
    catch { toast.error("Failed to update."); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this promo code? This cannot be undone.")) return;
    try { await api.delete(`/admin/discounts/${id}`); toast.success("Deleted."); mutate("/admin/discounts"); }
    catch { toast.error("Failed to delete."); }
  };
  const valueLabel = (d) => d.type === "percent" ? `${d.amount}% off` : `$${d.amount} off`;
  return (
    <div>
      <Header title="Promo Codes" sub="Create discount codes customers type at checkout. Applied before any gift card." />
      <div className="card p-6 mb-6" data-testid="promo-create">
        <div className="font-semibold text-[#f6ecd4] mb-4">Create a Promo Code</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div><label className="text-xs text-[#8fa5a5]">Code</label><input className={`${inp} mt-1`} placeholder="SUMMER20" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} data-testid="promo-code-input" /></div>
          <div><label className="text-xs text-[#8fa5a5]">Type</label>
            <select className={`${inp} mt-1`} value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="promo-type-select">
              <option value="percent">Percent (%)</option>
              <option value="fixed">Fixed ($)</option>
            </select>
          </div>
          <div><label className="text-xs text-[#8fa5a5]">{f.type === "percent" ? "Percent off" : "Dollars off"}</label><input type="number" min="1" className={`${inp} mt-1`} placeholder={f.type === "percent" ? "20" : "50"} value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="promo-amount-input" /></div>
          <div><label className="text-xs text-[#8fa5a5]">Expires (optional)</label><input type="date" className={`${inp} mt-1`} style={{ colorScheme: "dark" }} value={f.expires_at} onChange={(e) => setF({ ...f, expires_at: e.target.value })} data-testid="promo-expiry-input" /></div>
          <div><label className="text-xs text-[#8fa5a5]">Max uses (optional)</label><input type="number" min="1" className={`${inp} mt-1`} placeholder="∞" value={f.max_uses} onChange={(e) => setF({ ...f, max_uses: e.target.value })} data-testid="promo-maxuses-input" /></div>
        </div>
        <button onClick={create} className="btn-primary px-6 py-2.5 flex items-center gap-2 mt-4" data-testid="promo-create-btn"><Plus size={16} /> Create Code</button>
      </div>

      <div className="space-y-3">
        {(data || []).map((d) => (
          <div key={d.id} className="card p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`promo-item-${d.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono-arcade text-lg text-[#f6ecd4]">{d.code}</span>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#241a11] text-[#f6b64a] border border-[rgba(246,182,74,0.4)]">{valueLabel(d)}</span>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${d.active ? "bg-[#14241f] text-[#2fd0c6]" : "bg-[#2a1a12] text-[#8fa5a5]"}`}>{d.active ? "Active" : "Inactive"}</span>
              </div>
              <div className="text-xs text-[#8fa5a5] mt-1">
                Used {d.uses || 0}{d.max_uses ? ` / ${d.max_uses}` : ""} · {d.expires_at ? `Expires ${d.expires_at}` : "No expiry"}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => toggle(d)} data-testid={`promo-toggle-${d.id}`} className={`px-4 py-2 rounded-full text-xs font-mono-arcade font-bold border-2 ${d.active ? "bg-[#14241f] text-[#2fd0c6] border-[#2fd0c6]" : "bg-[#2a1a12] text-[#8fa5a5] border-[#33281c]"}`}>{d.active ? "ON" : "OFF"}</button>
              <button onClick={() => del(d.id)} className="text-[#ff5147] p-2" data-testid={`promo-del-${d.id}`}><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        {data && data.length === 0 && <div className="card p-10 text-center text-[#8fa5a5]">No promo codes yet. Create one above.</div>}
      </div>
    </div>
  );
}

